import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ImageWithFallback from './ImageWithFallback';
import { formatDateToGregorian } from '../utils/dateFormatter';
import { excerpt, parsePlace } from '../utils/place';

const MartyrCard = ({ martyr, featured = false }) => {
  const place = parsePlace(martyr.place_of_martyrdom);

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5 }}
      className={featured ? 'md:col-span-2' : ''}
    >
      <Link
        to={`/martyr/${martyr.id}`}
        className={`group block overflow-hidden rounded-3xl border border-gold-500/20 bg-white shadow-soft transition-all duration-500 hover:-translate-y-1.5 hover:shadow-large dark:bg-stone-900 dark:border-gold-500/10 ${
          featured ? 'md:grid md:grid-cols-2' : ''
        }`}
      >
        <div className={`relative overflow-hidden bg-stone-900 ${featured ? 'min-h-[320px] md:min-h-[420px]' : 'h-72'}`}>
          <ImageWithFallback
            src={martyr.image_url || '/default.png'}
            alt={martyr.name_ar}
            className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
            fallbackSrc="/default.png"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-0 right-0 left-0 p-6 text-white">
            <p className="mb-1 text-xs tracking-[0.2em] text-gold-300">شهيد الوطن</p>
            <h3 className={`font-bold leading-tight ${featured ? 'text-3xl md:text-4xl' : 'text-2xl'}`}>
              {martyr.name_ar}
            </h3>
            {martyr.name_en && <p className="mt-1 text-sm text-white/70">{martyr.name_en}</p>}
          </div>
        </div>
        <div className="flex flex-col justify-between space-y-4 p-6 md:p-8">
          <p className="text-stone-600 dark:text-stone-300 leading-relaxed">
            {excerpt(martyr.bio, featured ? 220 : 120) || `${martyr.occupation || 'شهيد'} — ${place.state || 'السودان'}`}
          </p>
          <div className="flex flex-wrap gap-3 text-sm text-stone-500 dark:text-stone-400">
            {martyr.date_of_martyrdom && (
              <span className="rounded-full bg-stone-100 px-3 py-1 dark:bg-stone-800">
                {formatDateToGregorian(martyr.date_of_martyrdom)}
              </span>
            )}
            {place.state && (
              <span className="rounded-full bg-stone-100 px-3 py-1 dark:bg-stone-800">{place.state}</span>
            )}
            {martyr.occupation && (
              <span className="rounded-full bg-primary-50 px-3 py-1 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
                {martyr.occupation}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-gold-700 dark:text-gold-400">
            اقرأ السيرة
            <span aria-hidden>←</span>
          </span>
        </div>
      </Link>
    </motion.article>
  );
};

export default MartyrCard;
